// =====================================================================
// Cuándo se miró por última vez · VEN-8
// =====================================================================
// POR QUE EXISTE, CON EL CASO. «Dinero en la mesa» abría, se pintaba
// entero y enseñaba cuatro ceros. Un cero ahí significa dos cosas que no
// se parecen en nada —«el proveedor cumplió» y «nadie ha mirado»— y la
// pantalla no las distinguía.
//
// Es la Regla 5 del encargo, «nunca un icono verde que miente», entrando
// por la puerta de atrás: lo que miente no es un icono, es un cero. Y de
// un icono verde la gente desconfía; de un cero, no.
//
// MEDIDO EN PRODUCCION EL 4 DE SEPTIEMBRE DE 2026: el temporizador
// `tego-cargos-proveedor` no está instalado en el servidor (VEN-7), así
// que el tablero lleva desde la v3.6.0 enseñando ceros que nadie escribió.
//
// =====================================================================
// TRES ESTADOS Y NINGUNO ES VERDE POR OMISION
// =====================================================================
// · `nunca`   — no consta ninguna revisión. No es un fallo: puede ser una
//               empresa recién dada de alta. Pero tampoco es normalidad,
//               así que va en amarillo y dice qué mirar.
// · `vieja`   — la última pasó hace más de `HORAS_DE_MARGEN`. La revisión
//               corre de madrugada, así que a las 36 horas ya se saltó
//               una noche entera.
// · `al_dia`  — corrió dentro del margen. Se dice igual, en gris y sin
//               alarma: saber que se miró es la mitad de poder creerse un
//               cero.
//
// POR QUE 36 HORAS Y NO 24. La barrida corre a las 03:40. A las 24 horas
// justas, una que se retrasara veinte minutos saldría marcada como vieja
// cada día — y una alarma que salta sin motivo se deja de mirar, que es
// el mismo daño que no tenerla.

export const HORAS_DE_MARGEN = 36;

/** Qué decir sobre la última revisión. `ahora` entra para poder probarlo.
 *
 * `revision` es lo que devuelve el tablero: `null` si nunca se ha
 * revisado, o `{ terminada_en, hasta, propuestas, caducados }`.
 */
export function estadoDeLaRevision(revision, ahora = new Date()) {
  if (!revision?.terminada_en) {
    return {
      estado: 'nunca',
      color: 'amarillo',
      titulo: 'Todavía no se ha revisado nada',
      detalle: 'Los cargos se detectan de madrugada, una vez al día. '
             + 'Mientras no haya corrido ninguna revisión, los números de '
             + 'abajo están en cero porque nadie ha mirado — no porque no '
             + 'haya nada que reclamar. Si mañana sigue igual, avisa a '
             + 'quien administra el servidor.',
    };
  }

  const cuando = new Date(revision.terminada_en);
  if (Number.isNaN(cuando.getTime())) {
    // UNA FECHA QUE NO SE ENTIENDE NO SE ENSEÑA COMO SI SE ENTENDIERA.
    // Se trata como si no constara, que es lo único que se sabe de ella.
    return estadoDeLaRevision(null, ahora);
  }

  const horas = (ahora - cuando) / 36e5;
  if (horas > HORAS_DE_MARGEN) {
    return {
      estado: 'vieja',
      color: 'amarillo',
      cuando,
      titulo: 'La última revisión no es de hoy',
      detalle: 'La revisión corre de madrugada, una vez al día, y la '
             + 'última pasó hace más de un día. Los números de abajo son '
             + 'los de entonces. Avisa a quien administra el servidor.',
    };
  }

  return {
    estado: 'al_dia',
    color: 'gris',
    cuando,
    titulo: 'Revisado',
    detalle: revision.propuestas
      ? `La última revisión encontró ${revision.propuestas} propuesta(s).`
      : 'La última revisión no encontró nada nuevo que reclamar.',
  };
}

/** «hoy a las 3:40», «ayer a las 3:40», o la fecha si es más viejo. */
export function cuandoEnPalabras(cuando, ahora = new Date()) {
  if (!cuando) return '';
  const hora = cuando.toLocaleTimeString('es-MX',
    { hour: '2-digit', minute: '2-digit' });
  const dia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dias = Math.round((dia(ahora) - dia(cuando)) / 864e5);
  if (dias === 0) return `hoy a las ${hora}`;
  if (dias === 1) return `ayer a las ${hora}`;
  return `${cuando.toLocaleDateString('es-MX')} a las ${hora}`;
}
